import * as React from "react"

const MOBILE_BREAKPOINT = 768

/**
 * Custom hook for detecting mobile device screen sizes using responsive breakpoints
 *
 * Monitors viewport width and returns true when the screen is below the mobile breakpoint (768px).
 * Automatically updates on window resize events using the matchMedia API for efficient responsive detection.
 *
 * **Note:** Uses a 768px breakpoint constant to determine mobile vs desktop layouts.
 *
 * @returns {boolean} True if viewport width is below 768px (mobile), false otherwise
 *
 * @example
 * // Use for conditional rendering based on screen size
 * function ResponsiveNav() {
 *   const isMobile = useIsMobile();
 *
 *   if (isMobile) {
 *     return <MobileNavigation />;
 *   }
 *
 *   return <DesktopNavigation />;
 * }
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    mql.addEventListener("change", onChange)
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return !!isMobile
}
